// =============================================================================
// MÓDULO FORM HANDLER
// =============================================================================

export class FormHandler {
    constructor(elements, config = {}) {
        this.elements = elements;
        this.config = {
            showThankYou: true,
            resetAfterSubmit: false,
            validateOnSubmit: true,
            submitDelay: 1000,
            ...config
        };

        this.init();
    }

    init() {
        this.elements.forEach(form => {
            this.setupForm(form);
        });
    }

    setupForm(form) {
        // Agregar event listeners
        form.addEventListener('submit', (e) => this.handleSubmit(e, form));
        
        // Validación en tiempo real
        if (this.config.validateOnSubmit) {
            this.setupRealTimeValidation(form);
        }

        // Setup para switches y elementos especiales
        this.setupFormElements(form);
    }

    setupRealTimeValidation(form) {
        const inputs = form.querySelectorAll('input, select, textarea');
        
        inputs.forEach(input => {
            input.addEventListener('blur', () => this.validateField(input));
            input.addEventListener('input', () => this.clearFieldError(input));
        });
    }

    setupFormElements(form) {
        // Setup para form switches
        const switches = form.querySelectorAll('.form-check-input[type="checkbox"]');
        switches.forEach(switchEl => {
            switchEl.addEventListener('change', (e) => {
                this.handleSwitchChange(e.target, form);
            });
        });

        // Setup para selects dependientes
        const dependentSelects = form.querySelectorAll('[data-depends-on]');
        dependentSelects.forEach(select => {
            this.setupDependentSelect(select, form);
        });
    }

    handleSwitchChange(switchEl, form) {
        const dependentFields = form.querySelectorAll(`[data-required-ruler="${switchEl.name}"]`);
        
        dependentFields.forEach(field => {
            if (switchEl.checked) {
                field.setAttribute('required', '');
            } else {
                field.removeAttribute('required');
                this.clearFieldError(field);
            }
        });
    }

    setupDependentSelect(select, form) {
        const dependsOn = select.dataset.dependsOn;
        const triggerElement = form.querySelector(`[name="${dependsOn}"]`);
        
        if (triggerElement) {
            triggerElement.addEventListener('change', () => {
                this.updateDependentSelect(select, triggerElement.value);
            });
        }
    }

    updateDependentSelect(select, triggerValue) {
        // Lógica para actualizar opciones del select dependiente
        // Esto se puede personalizar según las necesidades específicas
        const options = select.querySelectorAll('option');
        options.forEach(option => {
            const showFor = option.dataset.showFor;
            if (showFor) {
                option.style.display = showFor.includes(triggerValue) ? 'block' : 'none';
            }
        });
    }

    async handleSubmit(e, form) {
        e.preventDefault();

        // Validar formulario
        if (!this.validateForm(form)) {
            return;
        }

        // Mostrar loading
        this.showLoading(form);

        try {
            // Preparar datos
            const formData = new FormData(form);
            const data = this.prepareFormData(formData);

            // Enviar formulario
            const response = await this.submitForm(form.action, data);

            if (response.success) {
                this.handleSuccess(form, response);
            } else {
                this.handleError(form, response.error || 'Error al enviar el formulario');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            this.handleError(form, 'Error de conexión. Por favor, intenta nuevamente.');
        } finally {
            this.hideLoading(form);
        }
    }

    prepareFormData(formData) {
        const data = {};
        
        // Convertir FormData a objeto
        for (let [key, value] of formData.entries()) {
            data[key] = value;
        }

        // Agregar timestamp
        data.timestamp = new Date().toISOString();

        // Honeypot check
        if (data.honeypot && data.honeypot.trim() !== '') {
            throw new Error('Spam detected');
        }
        delete data.honeypot;

        return data;
    }

    async submitForm(action, data) {
        const response = await fetch(action, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }

    validateForm(form) {
        let isValid = true;
        const inputs = form.querySelectorAll('input[required], select[required], textarea[required]');

        inputs.forEach(input => {
            if (!this.validateField(input)) {
                isValid = false;
            }
        });

        return isValid;
    }

    validateField(field) {
        const value = field.value.trim();
        let isValid = true;
        let errorMessage = '';

        // Validación de campo requerido
        if (field.hasAttribute('required') && !value) {
            isValid = false;
            errorMessage = 'Este campo es requerido';
        }

        // Validación de email
        if (field.type === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
                errorMessage = 'Por favor ingresa un email válido';
            }
        }

        // Validación personalizada
        const customValidation = field.dataset.validation;
        if (customValidation && value) {
            const validationResult = this.runCustomValidation(customValidation, value);
            if (!validationResult.isValid) {
                isValid = false;
                errorMessage = validationResult.message;
            }
        }

        // Mostrar/ocultar error
        if (isValid) {
            this.clearFieldError(field);
        } else {
            this.showFieldError(field, errorMessage);
        }

        return isValid;
    }

    runCustomValidation(validationType, value) {
        switch (validationType) {
            case 'phone':
                const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
                return {
                    isValid: phoneRegex.test(value.replace(/\s/g, '')),
                    message: 'Por favor ingresa un teléfono válido'
                };
            default:
                return { isValid: true, message: '' };
        }
    }

    showFieldError(field, message) {
        this.clearFieldError(field);
        
        field.classList.add('is-invalid');
        
        const errorDiv = document.createElement('div');
        errorDiv.className = 'invalid-feedback';
        errorDiv.textContent = message;
        
        field.parentNode.appendChild(errorDiv);
    }

    clearFieldError(field) {
        field.classList.remove('is-invalid');
        
        const errorDiv = field.parentNode.querySelector('.invalid-feedback');
        if (errorDiv) {
            errorDiv.remove();
        }
    }

    showLoading(form) {
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Enviando...';
        }
    }

    hideLoading(form) {
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = submitBtn.dataset.originalText || 'Enviar';
        }
    }

    handleSuccess(form, response) {
        if (this.config.showThankYou) {
            this.showThankYouMessage(form);
        }

        if (this.config.resetAfterSubmit) {
            setTimeout(() => {
                form.reset();
                this.hideThankYouMessage(form);
            }, this.config.submitDelay);
        }

        // Trigger evento personalizado
        document.dispatchEvent(new CustomEvent('formSubmitSuccess', {
            detail: { form, response }
        }));
    }

    handleError(form, errorMessage) {
        const alertDiv = document.createElement('div');
        alertDiv.className = 'alert alert-danger alert-dismissible fade show';
        alertDiv.innerHTML = `
            ${errorMessage}
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;

        form.insertBefore(alertDiv, form.firstChild);

        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (alertDiv.parentNode) {
                alertDiv.remove();
            }
        }, 5000);
    }

    showThankYouMessage(form) {
        const formElements = form.querySelector('.rsvp__form-elements');
        const thankYouMessage = form.querySelector('.rsvp__thanks');

        if (formElements && thankYouMessage) {
            formElements.classList.add('d-none');
            thankYouMessage.classList.remove('d-none');
        }
    }

    hideThankYouMessage(form) {
        const formElements = form.querySelector('.rsvp__form-elements');
        const thankYouMessage = form.querySelector('.rsvp__thanks');

        if (formElements && thankYouMessage) {
            formElements.classList.remove('d-none');
            thankYouMessage.classList.add('d-none');
        }
    }
}